import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

export const CAR_SCREEN_WIDTH = 2560;
export const CAR_SCREEN_HEIGHT = 1600;

interface CarScreenViewportProps {
  children: ReactNode;
  className?: string;
}

export function CarScreenViewport({ children, className }: CarScreenViewportProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    const updateScale = () => {
      const { width, height } = container.getBoundingClientRect();

      if (width <= 0 || height <= 0) {
        setScale(0);
        return;
      }

      setScale(Math.min(width / CAR_SCREEN_WIDTH, height / CAR_SCREEN_HEIGHT));
    };

    const scheduleScaleUpdate = () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        updateScale();
      });
    };

    const refreshScaleAcrossLayoutFrames = () => {
      let remainingFrames = 6;
      const tick = () => {
        updateScale();
        remainingFrames -= 1;

        if (remainingFrames > 0) {
          frameRef.current = requestAnimationFrame(tick);
        } else {
          frameRef.current = null;
        }
      };

      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = requestAnimationFrame(tick);
    };

    refreshScaleAcrossLayoutFrames();

    const resizeObserver = new ResizeObserver(scheduleScaleUpdate);
    resizeObserver.observe(container);
    window.addEventListener("resize", refreshScaleAcrossLayoutFrames);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", refreshScaleAcrossLayoutFrames);
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <div
        style={{
          width: CAR_SCREEN_WIDTH,
          height: CAR_SCREEN_HEIGHT,
          flex: "0 0 auto",
          opacity: scale > 0 ? 1 : 0,
          transform: `scale(${scale})`,
          transformOrigin: "center center"
        }}
      >
        {children}
      </div>
    </div>
  );
}
